import type { AppShellIconKey } from "../../../../../../shared/frontend/layout/appShellIcons";
import type { AppShellPinnedPagesProps } from "../../../../../../shared/frontend/layout/AppShellPinnedPages";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePersonPhotoObjectUrls } from "../../pages/persons/usePersonPhotoObjectUrls";
import { ApiError } from "../api/client";
import { useAuth } from "../auth/useAuth";
import { HTTP_STATUS_FORBIDDEN, HTTP_STATUS_NOT_FOUND } from "../constants/httpStatus";


import { useI18n } from "../i18n/useI18n";
import { resolveRoutePinDisplay } from "../pins/pinnablePage";
import { pinInitials } from "../pins/pinnedLinks";
import type { PinnedLink } from "../pins/pinnedLinks";
import { usePinnedLinks } from "../pins/usePinnedLinks";
import { useGuardedAction } from "../utils/useGuardedAction";
import { normalizeMenuPath } from "./menu";
import type { MenuRoute } from "./menu";
import { useFeedback } from "../../../../../../shared/frontend/feedback/useFeedback";


/** Die Zahl der Pins ist nicht begrenzt — die Fotoladung schon, damit der App-Start nicht am Netz hängt. */
const PIN_PHOTO_LOAD_LIMIT = 12;

type ResolvedPin = {
  pin: PinnedLink;
  label: string;
  subtitle: string | null;
  icon: AppShellIconKey | null;
};

/** Persönliche Pins in der Titelleiste. */
export function usePinnedLinksConfiguration(props: { menu: MenuRoute[] }): Omit<AppShellPinnedPagesProps, "compact"> {
  const { menu } = props;
  const { text, tr } = useI18n();
  const feedback = useFeedback();
  const nav = useNavigate();
  const loc = useLocation();
  const auth = useAuth();
  const { items, removePin } = usePinnedLinks();

  /** Der Pin der gerade geöffneten Seite wird hervorgehoben — die Leiste zeigt so auch, wo man ist. */
  const activePath = normalizeMenuPath(loc.pathname || "");

  /** Fotos der gepinnten Personen — gleiche Batch-Ladung wie in den Personenlisten. */
  const photoIdentities = React.useMemo(
    () =>
      items
        .filter((pin) => pin.kind === "person" && pin.hasPhoto)
        .slice(0, PIN_PHOTO_LOAD_LIMIT)
        .map((pin) => ({ id: pin.targetId, hasPhoto: true })),
    [items]
  );
  const photoObjectUrls = usePersonPhotoObjectUrls(auth.api, photoIdentities);

  /**
   * Routen-Pins holen Beschriftung und Icon aus dem aktuellen Menü bzw. den Seitenkatalogen.
   * Bleibt beides leer, ist die Seite für diesen Benutzer nicht mehr erreichbar.
   */
  const { resolved, staleIds } = React.useMemo(() => {
    const out: ResolvedPin[] = [];
    const stale: string[] = [];
    for (const pin of items) {
      if (pin.kind === "person") {
        out.push({
          pin,
          label: pin.label || text("Person"),
          subtitle: pin.subtitleKey ? tr(pin.subtitleKey) : null,
          icon: null
        });
        continue;
      }
      const display = resolveRoutePinDisplay({ menu, path: pin.path, tr });
      if (!display) {
        stale.push(pin.id);
        continue;
      }
      out.push({ pin, label: display.label, subtitle: display.detail ?? null, icon: display.icon });
    }
    return { resolved: out, staleIds: stale };
  }, [items, menu, text, tr]);

  // Aufräumen nach Rechteverlust. Nur wenn das Menü bereits steht — ein leeres Menü
  // (noch nicht geladene Benutzerinfos) darf keine gültigen Pins löschen.
  const staleKey = staleIds.join("|");
  React.useEffect(() => {
    if (menu.length === 0) return;
    if (!staleKey) return;
    for (const id of staleKey.split("|")) removePin(id);
  }, [staleKey, menu.length, removePin]);

  const openPin = useGuardedAction(async (pin: PinnedLink) => {
    if (pin.kind === "person" && pin.targetId) {
      try {
        await auth.api.req(`/api/v1/persons/${encodeURIComponent(pin.targetId)}`, {
          method: "GET",
          suppressGlobalErrorToast: true
        });
      } catch (e: unknown) {
        if (e instanceof ApiError && (e.status === HTTP_STATUS_FORBIDDEN || e.status === HTTP_STATUS_NOT_FOUND)) {
          removePin(pin.id);
          feedback.warning(text("This page is no longer available"));
          return;
        }
        // Netz- oder Wartungsfehler: Pin behalten und trotzdem navigieren.
      }
    }
    nav(pin.path);
  });

  const renderAdornment = (entry: ResolvedPin) => entry.pin.kind === "person"
    ? { avatar: { src: entry.pin.targetId ? photoObjectUrls[entry.pin.targetId] : undefined, initials: pinInitials(entry.label) } }
    : { icon: entry.icon };
  return (
    ({
items: resolved.map(entry => ({
        id: entry.pin.id,
        label: entry.label,
        subtitle: entry.subtitle,
        ...renderAdornment(entry),
        active: normalizeMenuPath(entry.pin.path) === activePath,
      })),
      labels: { pinnedPages: text("Pinned pages"), removePin: text("Remove pin") },
      onOpen: (id) => {
        const entry = resolved.find(item => item.pin.id === id);
        if (entry) void openPin(entry.pin);
      },
      onRemove: removePin
})
  );
}
